/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CourseManagementCourseAccordion from "@stoneage/App/Components/CourseManagement/CourseManagementCourseAccordion";
import Notify from "@stoneage/App/Components/Notify";
import deleteRegisteredUser from "@stoneage/App/Handlers/CourseManagement/DeleteRegisteredUser";
import getCourses from "@stoneage/App/Handlers/CourseManagement/GetCourses";
import getRegisteredUsers from "@stoneage/App/Handlers/CourseManagement/GetRegisteredUsers";
import registerUser from "@stoneage/App/Handlers/CourseManagement/RegisterUser";
import updateRegisteredUser from "@stoneage/App/Handlers/CourseManagement/UpdateRegisteredUser";
import { CoursesModel } from "@stoneage/App/Services/CourseManagement/CourseManagementService";
import Pagination from "@stoneage/MobiusOverrides/Pagination/Pagination";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    getCourses,
    registerUser,
    getRegisteredUsers,
    updateRegisteredUser,
    deleteRegisteredUser,
};

type Props = ResolveThunks<typeof mapDispatchToProps>;

export interface CourseManagementCourseListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    coursesWrapper?: InjectableCss;
}

export const courseManagementCourseListStyles: CourseManagementCourseListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    coursesWrapper: {
        css: css`
            margin-bottom: 32px;
        `,
    },
};
const styles = courseManagementCourseListStyles;

const ThemeContextProvider = React.createContext<Function>(() => {});

export function useThemeContext() {
    return useContext(ThemeContextProvider);
}

const CourseManagementCourseList = ({
    getCourses,
    registerUser,
    getRegisteredUsers,
    updateRegisteredUser,
    deleteRegisteredUser,
}: Props) => {
    const [isLoading, setIsloading] = useState(false);
    const [courses, setCourses] = useState<CoursesModel[]>([]);
    const [updateCourses, setUpdateCourses] = useState<boolean>(false);
    const [paginationData, setPaginationData] = useState<PaginationModel | null>(null);

    const [sortData, setSortData] = useState({
        page: 1,
        pageSize: 8,
        sortBy: "courseNumber",
    });

    useEffect(() => {
        setIsloading(true);
        getCourses({
            onError: error => {
                setIsloading(false);
            },
            onSuccess(resultProps) {
                if (resultProps.successful) {
                    resultProps.result.courses && setCourses(resultProps.result.courses);
                    setPaginationData(resultProps.result.pagination);
                } else if (resultProps?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.errorMessage);
                }
                setIsloading(false);
            },
        });
    }, [updateCourses]);

    const getCoursesQuery = (newSortData: { page: number; pageSize: number; sortBy: string }) => {
        setIsloading(true);
        getCourses({
            ...newSortData,
            onError: error => {
                setIsloading(false);
            },
            onSuccess(resultProps) {
                if (resultProps.successful) {
                    resultProps.result.courses && setCourses(resultProps.result.courses);
                    setPaginationData(resultProps.result.pagination);
                } else if (resultProps?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.errorMessage);
                }
                setIsloading(false);
            },
        });
        setSortData(newSortData);
    };

    const changePage = (newPageIndex: number) => {
        const newSortData = {
            ...sortData,
            page: newPageIndex,
        };
        getCoursesQuery(newSortData);
    };
    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        const newSortData = {
            ...sortData,
            pageSize: newPageSize,
        };
        getCoursesQuery(newSortData);
    };

    const updateCourseStateAfterReclaim = (courseId: string) => {
        const index = courses.findIndex(course => course.id === courseId);
        if (index <= -1) {
            return;
        }
        const updatedCourse = courses[index];
        updatedCourse.seatsAvailable += 1;
        const newCourses = [...courses];
        newCourses[index] = updatedCourse;
        setCourses(newCourses);
    };

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }
    if (!courses.length) {
        return (
            <StyledWrapper {...styles.coursesWrapper}>
                <Notify messageType="info" body={translate("No Courses Found")} />
            </StyledWrapper>
        );
    }
    return (
        <ThemeContextProvider.Provider value={setUpdateCourses}>
            <StyledWrapper {...styles.coursesWrapper}>
                {courses &&
                    courses.map(course => (
                        <CourseManagementCourseAccordion
                            key={course.id}
                            course={course}
                            registerUser={registerUser}
                            getRegisteredUsers={getRegisteredUsers}
                            deleteRegisteredUser={deleteRegisteredUser}
                            updateRegisteredUser={updateRegisteredUser}
                            updateCourseState={updateCourseStateAfterReclaim}
                            expandOnCreate={updateCourses}
                        />
                    ))}
                {paginationData && paginationData.numberOfPages > 1 && (
                    <Pagination
                        resultsCount={paginationData.totalItemCount}
                        currentPage={sortData.page}
                        resultsPerPage={sortData.pageSize}
                        resultsPerPageOptions={paginationData.pageSizeOptions}
                        onChangePage={changePage}
                        onChangeResultsPerPage={changeResultsPerPage}
                        isBottom={true}
                        pageHasMultiplePaginations={true}
                    />
                )}
            </StyledWrapper>
        </ThemeContextProvider.Provider>
    );
};

export const definition: WidgetDefinition = {
    allowedContexts: ["CourseManagementPage"],
    displayName: "Course Management Course List",
    group: "Course Management" as any,
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CourseManagementCourseList),
    definition,
};

export default widgetModule;
